import { ApiError, apiFetch } from "@/lib/api/client";

export type ShadowStats = {
  periodStartedAt?: string | null;
  startedAt?: string | null;
  shadowAlertCount?: number;
  total?: number;
  unlabelledCount?: number;
  unlabelled?: number;
};

export type ShadowSummary = {
  sendMode?: string;
  periodStartedAt?: string | null;
  startedAt?: string | null;
  shadowAlertCount?: number;
  total?: number;
  unlabelledCount?: number;
  unlabelled?: number;
  shadowStats?: ShadowStats;
};

export type AdminOverview = {
  kpis: {
    totalUsers: number;
    newUsers24h: number;
    newUsers7d: number;
    activeUsers: number;
    verifiedPhonePct: number;
    verifiedEmailPct: number;
    usersInWarningSevereZone: number;
    alertsSentToday: number;
    deliverySuccessRate: number;
    spendThisMonthKes: number;
    spendByChannelKes: { sms: number; email: number; whatsapp: number };
  };
  pendingApprovals: number;
  activeAlerts: Array<{
    id: string;
    severity: string;
    status: string;
    areas?: string[];
    county?: string | null;
    createdAt: string;
  }>;
  recentAudit: Array<{
    id: string;
    action: string;
    actorEmail?: string | null;
    target?: string | null;
    createdAt: string;
  }>;
  pipelineHealth: Array<{
    id: string;
    name: string;
    status: string;
    lastSuccessAt?: string | null;
  }>;
  /** County / region aggregates — added by backend overview */
  usersByRegion?: Array<{ name: string; users: number; risk?: number }>;
  /** Delivery funnel counts — added by backend overview */
  deliveryPipeline?: Array<{ name: string; value: number; color?: string }>;
  /** Present when SHADOW_ALERTS is on or backend attaches shadow KPIs */
  sendMode?: string;
  shadowStats?: ShadowStats;
};

export type AlertDelivery = {
  id?: string;
  channel?: string;
  status?: string;
  recipientMasked?: string;
  errorMessage?: string | null;
  providerAcceptedAt?: string | null;
  sentAt?: string | null;
  deliveredAt?: string | null;
  receiptSimulated?: boolean;
  simulated?: boolean;
  webhookEvents?: Array<{ simulated?: boolean; raw?: { simulated?: boolean } }>;
};

export type TimeToReachRow = {
  recipients: number;
  reach: string;
  smsMinutes: number | null;
  pushMinutes?: number | null;
  status?: string;
};

export type TimeToReachTable = {
  measured?: boolean;
  rps?: number | null;
  note?: string;
  rows: TimeToReachRow[];
};

export type UserPlaceRisk = {
  placeId: string;
  label: string;
  ward?: string | null;
  county?: string | null;
  severity: string;
  rain24hMm?: number | null;
  riverDischargeRatio?: number | null;
  updatedAt?: string | null;
};

export type UserPlaceRisks = {
  places: UserPlaceRisk[];
  forecast72?: Array<{
    hour: string;
    label?: string;
    rainMm: number;
    risk?: string;
    reason?: "river" | "rain" | null;
    level?: string;
    hint?: string;
  }>;
  locationRequired?: boolean;
  scope?: string;
  forecastSummary?: {
    title?: string;
    subtitle?: string;
    unit?: string;
    placeHint?: string | null;
  } | null;
  season?: { label: string; banner: string } | null;
  source?: string;
};

export type DispatchLine = {
  id: string;
  area: string;
  recipient: string;
  channel: string;
  ok: boolean;
  status?: string;
  reason?: string | null;
  alreadySent?: boolean;
};

export type PendingAlert = {
  id: string;
  severity: string;
  status: string;
  areas: string[];
  county?: string | null;
  channelSkipReport?: { retryable?: boolean } | null;
  messageEn?: string | null;
  createdAt: string;
  recipientEstimate?: number | null;
};

export type UserOverview = {
  user: {
    id: string;
    name?: string | null;
    county?: string | null;
    ward?: string | null;
    language?: string | null;
  };
  places: Array<{ id: string; label: string; county?: string | null; ward?: string | null }>;
  activeAlerts: Array<{
    id: string;
    severity: string;
    areas: string[];
    messageEn?: string | null;
    isAdvisory?: boolean;
    createdAt: string;
  }>;
  currentRisk: Record<string, unknown> | null;
};

export type AdminUserRow = {
  id: string;
  name?: string | null;
  phone?: string | null;
  email?: string | null;
  county?: string | null;
  ward?: string | null;
  language?: string | null;
  phoneVerified?: boolean;
  emailVerified?: boolean;
  consentStatus?: string | null;
  personas: string[];
  channels?: { sms?: boolean; email?: boolean; whatsapp?: boolean };
  savedPlacesCount?: number;
  currentLocation?: {
    ward?: string | null;
    county?: string | null;
    capturedAt?: string;
    live?: boolean;
  } | null;
  signupDate?: string;
  lastActive?: string | null;
  status?: string;
};

export const liveApi = {
  adminOverview: () => apiFetch<AdminOverview>("/admin/overview", { audience: "admin" }),
  adminPendingAlerts: () =>
    apiFetch<{ alerts: PendingAlert[] }>("/admin/alerts/pending", { audience: "admin" }),
  adminAudit: () =>
    apiFetch<{ entries: Array<Record<string, unknown>> }>("/admin/audit?pageSize=20", {
      audience: "admin",
    }),
  adminUsers: (params?: { q?: string; persona?: string; page?: number; pageSize?: number }) => {
    const sp = new URLSearchParams();
    if (params?.q) sp.set("q", params.q);
    if (params?.persona) sp.set("persona", params.persona);
    if (params?.page) sp.set("page", String(params.page));
    if (params?.pageSize) sp.set("pageSize", String(params.pageSize));
    const qs = sp.toString();
    return apiFetch<{
      page: number;
      pageSize: number;
      total: number;
      personaCounts?: {
        driver: number;
        rider: number;
        resident: number;
        other: number;
        unset: number;
      };
      users: AdminUserRow[];
    }>(`/admin/users${qs ? `?${qs}` : ""}`, { audience: "admin" });
  },
  adminAlerts: (params?: { page?: number; pageSize?: number; status?: string }) => {
    const sp = new URLSearchParams();
    if (params?.page) sp.set("page", String(params.page));
    if (params?.pageSize) sp.set("pageSize", String(params.pageSize));
    if (params?.status) sp.set("status", params.status);
    const qs = sp.toString();
    return apiFetch<{ page: number; pageSize: number; total: number; alerts: PendingAlert[] }>(
      `/admin/alerts${qs ? `?${qs}` : ""}`,
      { audience: "admin" },
    );
  },
  createManualAlert: (body: {
    county: string;
    ward?: string;
    severity: "LOW" | "WATCH" | "WARNING" | "SEVERE";
    messageEn: string;
    hazard?: "earthquake" | "fire" | "wind" | "heat" | "lightning" | "drought";
    durationHours?: 24 | 48 | 72;
    validUntil?: string;
  }) =>
    apiFetch<{ alert: { id: string } }>("/admin/alerts", {
      method: "POST",
      audience: "admin",
      body: JSON.stringify(body),
    }),

  approveAlert: (id: string) =>
    apiFetch<{
      alert: PendingAlert;
      dispatch?: DispatchLine[];
      deliveries?: AlertDelivery[];
      skips?: Array<{ reason?: string; channel?: string; count?: number }>;
      providerConfirmed?: number;
    }>(`/admin/alerts/${id}/approve`, {
      method: "POST",
      audience: "admin",
      body: JSON.stringify({}),
    }),
  resumeAlert: (id: string) =>
    apiFetch<{
      alert: PendingAlert;
      dispatch?: DispatchLine[];
      deliveries?: AlertDelivery[];
      skips?: Array<{ reason?: string }>;
      alreadyRunning?: boolean;
    }>(`/admin/alerts/${id}/resume-send`, {
      method: "POST",
      audience: "admin",
      body: JSON.stringify({}),
    }),
  fanoutPreview: (id: string) =>
    apiFetch<{
      alertId: string;
      eligibleSmsCount: number;
      eta?: {
        unmeasured?: boolean;
        summary?: string;
        smsMinutes?: number | null;
        rps?: number | null;
        timeToReachTable?: TimeToReachTable;
      };
      timeToReachTable?: TimeToReachTable;
    }>(`/admin/alerts/${id}/fanout-preview`, { audience: "admin" }),
  suppressAlert: (id: string, reason: string) =>
    apiFetch<{ alert: PendingAlert }>(`/admin/alerts/${id}/suppress`, {
      method: "POST",
      audience: "admin",
      body: JSON.stringify({ reason }),
    }),
  rejectAlert: (id: string, reason: string) =>
    apiFetch<{ alert: PendingAlert }>(`/admin/alerts/${id}/reject`, {
      method: "POST",
      audience: "admin",
      body: JSON.stringify({ reason }),
    }),
  userOverview: () => apiFetch<UserOverview>("/user/overview", { audience: "user" }),
  userPlaceRisks: () => apiFetch<UserPlaceRisks>("/user/places/risk", { audience: "user" }),
  riskAt: (body: {
    latitude: number;
    longitude: number;
    county?: string;
    ward?: string;
    label?: string;
  }) =>
    apiFetch<{
      severity: string;
      rain24hMm?: number | null;
      riverDischargeRatio?: number | null;
      advice: string;
      county?: string | null;
      ward?: string | null;
    }>("/user/risk/at", {
      method: "POST",
      audience: "user",
      body: JSON.stringify(body),
    }),
  userAlerts: () =>
    apiFetch<{
      alerts: Array<{
        id: string;
        severity: string;
        status?: string;
        areas?: string[];
        county?: string | null;
        messageEn?: string | null;
        messageSw?: string | null;
        body?: string | null;
        bodyLang?: string;
        title?: string;
        advice?: string;
        isAdvisory?: boolean;
        isOfficial?: boolean;
        officialSourceUrl?: string | null;
        officialSourceName?: string | null;
        dataAgeAt?: string | null;
        createdAt: string;
        sentAt?: string | null;
        validUntil?: string | null;
        kind?: string;
      }>;
      officialWarnings?: Array<{
        id: string;
        headline: string;
        body: string;
        sourceName: string;
        receivedAt: string;
        county?: string | null;
        areas?: string[];
        kind?: string;
      }>;
      roadHazards?: Array<{
        id: string;
        alertId?: string | null;
        title: string;
        body?: string;
        advice?: string;
        severity?: string;
        county?: string | null;
        ward?: string | null;
        placeLabel?: string | null;
        latitude?: number;
        longitude?: number;
        createdAt?: string;
        impassable?: boolean;
        kind?: string;
      }>;
      deliveries?: Array<{
        id: string;
        channel: string;
        status: string;
        alertId: string;
        severity: string;
        body?: string | null;
        areas?: string[];
        county?: string | null;
        sentAt?: string | null;
        isOfficial?: boolean;
        kind?: string;
      }>;
      fetchedAt?: string;
      messageLanguage?: string;
      minSeverity?: string;
      scope?: string;
      locationRequired?: boolean;
    }>("/user/alerts", { audience: "user" }),
  userMapLayers: () =>
    apiFetch<{
      zones: Array<{
        id: string;
        name: string;
        riskClass: string;
        polygon: unknown;
        county?: string;
        notes?: string | null;
        summary?: string;
        advice?: string;
        kind?: string;
      }>;
      alerts: Array<{
        id: string;
        severity: string;
        status: string;
        polygon: unknown;
        areas?: string[];
        county?: string | null;
        messageEn?: string | null;
        title?: string;
        advice?: string;
        dataAgeAt?: string | null;
        createdAt?: string;
        isOfficial?: boolean;
        isAdvisory?: boolean;
      }>;
      closures: unknown[];
      officialWarnings: Array<{
        id: string;
        headline: string;
        body: string;
        sourceName: string;
        receivedAt: string;
        county?: string | null;
        areas?: string[];
      }>;
      activeAlerts?: Array<{
        id: string;
        severity: string;
        hazardType?: string;
        polygon?: unknown;
        h3Cell?: string | null;
        sentAt?: string | null;
        validUntil?: string | null;
        source?: string;
        isOfficial?: boolean;
        officialSourceName?: string | null;
        officialSourceUrl?: string | null;
        county?: string | null;
        areas?: string[];
        title?: string;
        body?: string;
        advice?: string;
      }>;
      approvedReports?: Array<{
        id: string;
        hazardType?: string;
        severity?: string | null;
        placeLabel?: string | null;
        county?: string | null;
        ward?: string | null;
        latitude: number;
        longitude: number;
        description?: string;
        createdAt: string;
      }>;
      counts?: {
        riskAreas: number;
        activeAlerts: number;
        approvedReports: number;
      };
      kenyaMetConnected: boolean;
      fetchedAt: string;
      conditions?: Array<{
        id: string;
        county: string;
        ward: string;
        latitude: number;
        longitude: number;
        userCount: number;
        severity: string;
        condition: string;
        rain24hMm?: number | null;
        label: string;
      }>;
      hazards?: Array<{
        id: string;
        hazard: string;
        severity: string;
        county?: string;
        ward?: string;
        latitude: number;
        longitude: number;
        title: string;
        body?: string | null;
      }>;
      focus?: {
        counties: string[];
        wards: string[];
        places?: Array<{ id?: string; label?: string; ward?: string; county?: string }>;
      };
      scope?: string;
    }>("/user/map/layers", { audience: "user" }),
  savePreferences: (body: Record<string, unknown>) =>
    apiFetch<{ preferences: Record<string, unknown> }>("/me/prefs", {
      method: "PATCH",
      audience: "user",
      body: JSON.stringify(body),
    }),
  mapLayers: () =>
    apiFetch<{
      zones: Array<{
        id: string;
        name: string;
        riskClass: string;
        polygon: unknown;
        county?: string;
      }>;
      alerts: Array<{
        id: string;
        severity: string;
        status: string;
        polygon: unknown;
        areas?: string[];
        county?: string | null;
        messageEn?: string | null;
        dataAgeAt?: string | null;
        createdAt?: string;
        isOfficial?: boolean;
        isAdvisory?: boolean;
      }>;
      closures: unknown[];
      officialWarnings: Array<{
        id: string;
        headline: string;
        body: string;
        sourceName: string;
        receivedAt: string;
      }>;
      kenyaMetConnected: boolean;
      fetchedAt: string;
      conditions?: Array<{
        id: string;
        county: string;
        ward: string;
        latitude: number;
        longitude: number;
        userCount: number;
        severity: string;
        condition: "clear" | "drizzle" | "rain" | "storm" | string;
        rain24hMm?: number | null;
        label: string;
      }>;
      hazards?: Array<{
        id: string;
        hazard: string;
        severity: string;
        county?: string;
        ward?: string;
        latitude: number;
        longitude: number;
        title: string;
        body?: string | null;
        validUntil?: string | null;
      }>;
    }>("/admin/map/layers", { audience: "admin" }),
  adminRules: () =>
    apiFetch<{ rules: Array<Record<string, unknown>> }>("/admin/rules", { audience: "admin" }),
  patchRule: (id: string, body: Record<string, unknown>) =>
    apiFetch<{ rule: Record<string, unknown> }>(`/admin/rules/${id}`, {
      method: "PATCH",
      audience: "admin",
      body: JSON.stringify(body),
    }),
  createRule: (body: Record<string, unknown>) =>
    apiFetch<{ rule: Record<string, unknown> }>("/admin/rules", {
      method: "POST",
      audience: "admin",
      body: JSON.stringify(body),
    }),
  adminRainfall: () =>
    apiFetch<{
      snapshots: Array<{
        id: string;
        county: string;
        ward?: string | null;
        severity: string;
        rain1hMm?: number | null;
        rain24hMm?: number | null;
        rain72hMm?: number | null;
        forecastAt: string;
      }>;
      fetchedAt: string;
    }>("/admin/map/rainfall", { audience: "admin" }),
  officialWarnings: () =>
    apiFetch<{
      connected: boolean;
      banner: string | null;
      warnings: Array<Record<string, unknown>>;
    }>("/admin/official-warnings", { audience: "admin" }),
  createOfficialWarning: (body: Record<string, unknown>) =>
    apiFetch<{ warning: Record<string, unknown>; alert: Record<string, unknown> }>(
      "/admin/official-warnings",
      { method: "POST", audience: "admin", body: JSON.stringify(body) },
    ),
  alertDetail: (id: string) =>
    apiFetch<{
      alert: Record<string, unknown> & { deliveries?: AlertDelivery[] };
      deliveries?: AlertDelivery[];
    }>(`/admin/alerts/${id}`, { audience: "admin" }),
  /**
   * Shadow-mode KPIs for the ops banner.
   * Returns null when the endpoint is not deployed yet (404).
   */
  adminShadowSummary: async (): Promise<ShadowSummary | null> => {
    try {
      return await apiFetch<ShadowSummary>("/admin/shadow-summary", { audience: "admin" });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },

  pilotStatus: () => apiFetch<PilotStatus>("/admin/pilot/status", { audience: "admin" }),

  pilotGates: () =>
    apiFetch<{ gates: PilotGateRow[] }>("/admin/pilot/gates", { audience: "admin" }),

  pilotSignOffGate: (key: string, body: { evidenceNote: string; evidenceUrl?: string }) =>
    apiFetch<{ ok: boolean; gate: PilotGateRow }>(
      `/admin/pilot/gates/${encodeURIComponent(key)}/sign-off`,
      {
        method: "POST",
        audience: "admin",
        body: JSON.stringify(body),
      },
    ),

  pilotPause: (body: {
    scope: "GLOBAL" | "COUNTY" | "CHANNEL";
    county?: string;
    channel?: string;
    reason: string;
  }) =>
    apiFetch<{ ok: boolean; pause: PilotPauseRow }>("/admin/pilot/pause", {
      method: "POST",
      audience: "admin",
      body: JSON.stringify(body),
    }),

  pilotResume: (body: { pauseId: string; reason?: string }) =>
    apiFetch<{ ok: boolean }>("/admin/pilot/resume", {
      method: "POST",
      audience: "admin",
      body: JSON.stringify(body),
    }),

  alertAckGet: (t: string) =>
    apiFetch<{
      ok: boolean;
      alertId: string;
      deliveryId: string;
      severity?: string;
      county?: string;
      acknowledgedAt?: string | null;
      expiresAt?: string;
    }>(`/public/alert-ack?t=${encodeURIComponent(t)}`),

  alertAckPost: (body: {
    t: string;
    action: "ack" | "feedback";
    outcome?: "YES_FLOODING" | "NO_FLOODING" | "NOT_SURE";
  }) =>
    apiFetch<{ ok: boolean; action: string }>("/public/alert-ack", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};

export type PilotPauseRow = {
  id: string;
  scope: string;
  county?: string | null;
  channel?: string | null;
  active: boolean;
  reason: string;
  pausedBy?: string;
  heldCount?: number;
};

export type PilotGateRow = {
  key: string;
  name: string;
  kind: string;
  owner: string;
  stageRequired: string;
  status: string;
  evidenceNote?: string | null;
  signedOffBy?: string | null;
  signedOffAt?: string | null;
  machineResult?: { ok: boolean; detail: string } | null;
};

export type PilotStatus = {
  stage: string;
  allowsLiveSend: boolean;
  pauses: PilotPauseRow[];
  cohortSize: number;
  maxUsers: number;
  countyAllowlist: string[];
  gateSummary: {
    total: number;
    green: number;
    pending: number;
    red: number;
    blockedHuman: number;
    blockingForCurrentStage: number;
    failures: Array<{ key: string; status: string }>;
  };
  provider?: Record<string, unknown>;
  features?: { liveAlerts?: boolean; shadowAlerts?: boolean };
};
